import React, { useEffect, useRef, useState } from 'react';
import { apiClient, errorMessage } from '../api';
import { getLoginLockup, revealLoginSplash } from './loginSplash';
import './Login.css';

// El lockup del logo reveal de carga (símbolo y palabra «Zafiro»): el reveal viaja hasta acá y este queda en su
// lugar. El marcado y los estilos son los de public/index.html.
const LoginLockup = () => {
    // Una sola vez por montaje: el adelanto de las animaciones no puede cambiar después. El objeto `__html` también
    // queda fijo: React vuelve a escribir el innerHTML si le llega uno nuevo, aunque diga lo mismo, y eso recrea el
    // logo y reinicia sus animaciones en cada render (cada tecla del formulario).
    const [lockup] = useState(() => { const { html, style } = getLoginLockup(); return { style, markup: html ? { __html: html } : null }; });
    if (!lockup.markup) return <div className="login-lockup login-lockup--text">Zafiro</div>;
    return <div className="login-lockup" role="img" aria-label="Zafiro" style={lockup.style} dangerouslySetInnerHTML={lockup.markup} />;
};

// Pantalla de login con el aspecto de la de Zafiro Web: el lockup como encabezado y, debajo, el panel con el
// formulario como continuación del logo reveal de carga. Se entra con Microsoft; el usuario y contraseña es sólo
// el de emergencia (admin). `initialMessage`: por qué Microsoft no dejó entrar, si se vuelve de ahí.
export default function LoginStage({ onLogin, initialMessage = '' }) {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [message, setMessage] = useState(initialMessage);
    const [sending, setSending] = useState(false);
    // Qué ingresos ofrece el servidor. Hasta saberlo (o si no contesta) queda el formulario: es el de emergencia.
    const [options, setOptions] = useState({ microsoft: false, local: true });
    const usernameInput = useRef(null);
    // El login ya muestra su pantalla definitiva: el logo reveal de carga se abre sobre ella. El foco va al usuario
    // recién cuando el reveal se retiró: con autoFocus el navegador abría su lista de contraseñas guardadas encima.
    useEffect(() => { revealLoginSplash().then(() => usernameInput.current?.focus()); }, []);
    useEffect(() => {
        Promise.resolve(apiClient.get('/api/auth/options')).then(res => setOptions({ microsoft: res?.data?.microsoft === true, local: res?.data?.local !== false })).catch(() => {});
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        setSending(true);
        setMessage('');
        try { const res = await apiClient.post('/api/login', { username, password }); onLogin(res.data.access_token, res.data.user); }
        catch (err) { setMessage(errorMessage(err, 'Error al iniciar sesión.')); setSending(false); }
    };

    const form = (
        <form onSubmit={submit} className="login-form">
            <input value={username} onChange={e => setUsername(e.target.value)} placeholder="Usuario" aria-label="Usuario" ref={usernameInput} autoComplete="username" required className="login-input" />
            <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Contraseña" aria-label="Contraseña" autoComplete="current-password" required className="login-input" />
            <button type="submit" disabled={sending} className="login-submit-button">{sending ? 'Ingresando…' : 'Iniciar sesión'}</button>
        </form>
    );

    return (
        <div className="login-page">
            <section className="login-card">
                <header className="login-card__brand">
                    <LoginLockup />
                    <span className="login-card__brand-version">Update Manager</span>
                    <p className="login-card__brand-caption">Liberación de updates de Zafiro a los clientes, por versión.</p>
                </header>

                <div className="login-card__form">
                    <div className="login-card__heading">
                        <span className="login-card__eyebrow">Acceso</span>
                        <h1>Update Manager</h1>
                        <p className="login-card__subtitle">{options.microsoft ? 'Ingresá con tu cuenta de Microsoft de la empresa.' : 'Ingresá con tu usuario de la herramienta.'}</p>
                    </div>
                    {/* Un enlace y no un pedido: el navegador va a Microsoft y vuelve con la sesión (ver App.jsx). */}
                    {options.microsoft && <a href="/api/auth/microsoft/login" className="login-submit-button login-microsoft-button">Entrar con Microsoft</a>}
                    {options.local && (options.microsoft
                        ? <details className="login-emergency"><summary>Usuario de emergencia</summary>{form}</details>
                        : form)}
                    <p role="alert" className="login-message">{message}</p>
                </div>
            </section>
            <p className="login-footer">Copyright © InfOil {new Date().getFullYear()}</p>
        </div>
    );
}
